import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CATEGORIES_OPCVM,
  Decimal,
  LIBELLE_CATEGORIE_OPCVM,
  ZERO,
  formaterDate,
  formaterPourcentage,
  somme,
  type CategorieOpcvm,
  type Poche,
  type StatutAllocation,
} from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { api, ErreurApi } from '../api/client';
import { useAuth } from '../auth/contexte';
import { Bloc } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille } from '../composants/Pastille';
import { RegleAllocation, type LigneReglette } from '../composants/RegleAllocation';
import { LIBELLE_ALLOCATION, TON_ALLOCATION } from './Allocations';

interface Ligne {
  id: string;
  poche: Poche;
  instrumentId: string | null;
  trancheMaturiteId: string | null;
  categorieOpcvm: CategorieOpcvm | null;
  pourcentage: string;
  instrument: { libelle: string } | null;
  tranche: { libelle: string } | null;
}

interface Allocation {
  id: string;
  profilId: string;
  version: number;
  dateEffet: string;
  statut: StatutAllocation;
  commentaire: string | null;
  creePar: string;
  total: string;
  poidsPoches: Record<Poche, string>;
  profil: { code: string; libelle: string; charia: boolean };
  lignes: Ligne[];
}

type Anomalie = { code: string; message: string };
type Instrument = { id: string; libelle: string; type: string; statut: string };
type Tranche = { id: string; libelle: string; ordre: number };

/** Saisie locale d'une ligne, avant enregistrement. */
interface LigneSaisie {
  cle: string;
  poche: Poche;
  reference: string;
  pourcentage: string;
}

const LIBELLE_POCHE: Record<Poche, string> = {
  ACTIONS: 'Poche actions',
  TAUX: 'Poche taux · BDT',
  OPCVM: 'Poche OPCVM',
};

export function FicheAllocation() {
  const { id } = useParams<{ id: string }>();
  const { profil: utilisateur, aRole } = useAuth();
  const allocation = useRessource<Allocation>(`/allocations/${id}`);
  const controle = useRessource<Anomalie[]>(`/allocations/${id}/controle?validation=true`);
  const instruments = useRessource<Instrument[]>('/instruments?type=ACTION');
  const tranches = useRessource<Tranche[]>('/tranches-maturite');

  const [edition, setEdition] = useState<LigneSaisie[] | null>(null);
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const a = allocation.donnees;
  const anomalies = controle.donnees ?? [];
  const peutEditer = aRole('GERANT', 'RESP_GESTION', 'ADMIN') && a?.statut === 'BROUILLON';
  const peutValider = aRole('RESP_GESTION') && a?.statut === 'A_VALIDER';
  // Même exception qu'au mandat : l'ADMIN valide ses propres opérations
  // (décision du 04/08/2026), le serveur la porte déjà.
  const estAuteur = utilisateur?.id === a?.creePar && !aRole('ADMIN');

  const reglettes = useMemo<Record<Poche, LigneReglette[]>>(() => {
    const vide: Record<Poche, LigneReglette[]> = { ACTIONS: [], TAUX: [], OPCVM: [] };
    if (!a) return vide;
    for (const l of a.lignes) {
      vide[l.poche].push({
        cle: l.id,
        libelle:
          l.instrument?.libelle ??
          l.tranche?.libelle ??
          (l.categorieOpcvm ? LIBELLE_CATEGORIE_OPCVM[l.categorieOpcvm] : '—'),
        // Sans portefeuille, la barre du réel n'a pas de sens : elle
        // apparaîtra sur la fiche d'un mandat, au lot 7.
        reelPct: null,
        ciblePct: l.pourcentage,
      });
    }
    return vide;
  }, [a]);

  function commencerEdition() {
    if (!a) return;
    setEdition(
      a.lignes.map((l) => ({
        cle: l.id,
        poche: l.poche,
        reference: l.instrumentId ?? l.trancheMaturiteId ?? l.categorieOpcvm ?? '',
        pourcentage: new Decimal(l.pourcentage).toString(),
      })),
    );
  }

  async function enregistrer() {
    if (!edition || !a) return;
    setErreurAction(null);
    setOccupe(true);
    try {
      // PUT, pas PATCH : les lignes envoyées remplacent l'intégralité de la
      // grille du brouillon, elles ne la complètent pas.
      await api.put(`/allocations/${a.id}/lignes`, {
        lignes: edition.map((l) => ({
          poche: l.poche,
          instrumentId: l.poche === 'ACTIONS' ? l.reference : undefined,
          trancheMaturiteId: l.poche === 'TAUX' ? l.reference : undefined,
          categorieOpcvm: l.poche === 'OPCVM' ? l.reference : undefined,
          pourcentage: l.pourcentage,
        })),
      });
      setEdition(null);
      await Promise.all([allocation.recharger(), controle.recharger()]);
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Enregistrement impossible.');
    } finally {
      setOccupe(false);
    }
  }

  async function agir(appel: () => Promise<unknown>) {
    setErreurAction(null);
    setOccupe(true);
    try {
      await appel();
      await Promise.all([allocation.recharger(), controle.recharger()]);
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Opération impossible.');
    } finally {
      setOccupe(false);
    }
  }

  if (allocation.chargement) return <p className="surtitre">Chargement…</p>;
  if (allocation.erreur || !a)
    return (
      <BandeauAlerte nature="refus" titre="Version introuvable">
        {messageErreur(allocation.erreur) ?? 'Cette version n’existe pas.'}
      </BandeauAlerte>
    );

  // Une saisie en cours peut être vide ou incomplète : elle compte pour zéro.
  const totalEdition = edition
    ? somme(
        edition.map((l) =>
          /^\d+(\.\d+)?$/.test(l.pourcentage) ? new Decimal(l.pourcentage) : ZERO,
        ),
      )
    : new Decimal(a.total);

  return (
    <>
      <p className="entete__fil">
        <Link to="/allocations">Allocations cibles</Link> › {a.profil.libelle} v{a.version}
      </p>
      <div className="entete">
        <h1>{a.profil.libelle}</h1>
        <span className="entete__ref">version {a.version}</span>
        <Pastille ton={TON_ALLOCATION[a.statut]}>{LIBELLE_ALLOCATION[a.statut]}</Pastille>
        {a.profil.charia && <Pastille ton="interface">Charia</Pastille>}
      </div>

      <section className="cles" aria-label="Répartition">
        {(['ACTIONS', 'TAUX', 'OPCVM'] as Poche[]).map((poche) => (
          <div className="cle" key={poche}>
            <span className="surtitre">{LIBELLE_POCHE[poche]}</span>
            <div className="cle__valeur">{formaterPourcentage(a.poidsPoches[poche])}</div>
          </div>
        ))}
        <div className="cle">
          <span className="surtitre">Total</span>
          <div className={`cle__valeur ${new Decimal(a.total).eq(100) ? '' : 'valeur-a-revoir'}`}>
            {formaterPourcentage(a.total)}
          </div>
          <div className="appoint">date d’effet {formaterDate(a.dateEffet)}</div>
        </div>
      </section>

      {erreurAction && (
        <BandeauAlerte nature="refus" titre="Opération refusée">
          {erreurAction}
        </BandeauAlerte>
      )}

      {anomalies.length > 0 ? (
        <BandeauAlerte nature="gestion" titre="Anomalies bloquant la validation">
          {anomalies.map((x) => x.message).join(' ')}
        </BandeauAlerte>
      ) : (
        a.statut === 'BROUILLON' && (
          <BandeauAlerte nature="information" titre="Version cohérente">
            La somme vaut 100 %, aucune anomalie. Cette version peut être soumise à validation.
          </BandeauAlerte>
        )
      )}

      {peutValider && estAuteur && (
        <BandeauAlerte nature="gestion" titre="Validation impossible par vous">
          Vous avez préparé cette version : sa validation revient à une autre personne.
        </BandeauAlerte>
      )}

      {!edition &&
        (['ACTIONS', 'TAUX', 'OPCVM'] as Poche[]).map((poche) =>
          reglettes[poche].length > 0 ? (
            <Bloc
              key={poche}
              titre={LIBELLE_POCHE[poche]}
              meta={formaterPourcentage(a.poidsPoches[poche])}
            >
              <RegleAllocation lignes={reglettes[poche]} />
            </Bloc>
          ) : null,
        )}

      {edition && (
        <Bloc titre="Édition des lignes" meta={`total ${formaterPourcentage(totalEdition)}`}>
          {edition.map((l, index) => (
            <div key={l.cle} className="saisie-ligne saisie-ligne--allocation">
              <span className="surtitre">{l.poche}</span>
              <select
                className="champ__select"
                value={l.reference}
                onChange={(e) => {
                  const copie = [...edition];
                  copie[index] = { ...l, reference: e.target.value };
                  setEdition(copie);
                }}
              >
                <option value="">—</option>
                {l.poche === 'ACTIONS' &&
                  (instruments.donnees ?? []).map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.libelle}
                      {i.statut !== 'ACTIF' ? ` (${i.statut})` : ''}
                    </option>
                  ))}
                {l.poche === 'TAUX' &&
                  (tranches.donnees ?? []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.libelle}
                    </option>
                  ))}
                {l.poche === 'OPCVM' &&
                  CATEGORIES_OPCVM.map((c) => (
                    <option key={c} value={c}>
                      {LIBELLE_CATEGORIE_OPCVM[c]}
                    </option>
                  ))}
              </select>
              <input
                className="saisie-chiffre"
                aria-label="Pourcentage"
                value={l.pourcentage}
                onChange={(e) => {
                  const copie = [...edition];
                  copie[index] = { ...l, pourcentage: e.target.value };
                  setEdition(copie);
                }}
              />
              <button
                className="bouton bouton--fantome bouton--compact"
                onClick={() => setEdition(edition.filter((_, i) => i !== index))}
                aria-label="Retirer la ligne"
              >
                ×
              </button>
            </div>
          ))}

          <div className="barre-actions barre-actions--apres">
            {(['ACTIONS', 'TAUX', 'OPCVM'] as Poche[]).map((poche) => (
              <button
                key={poche}
                className="bouton bouton--fantome"
                onClick={() =>
                  setEdition([
                    ...edition,
                    { cle: `neuve-${Date.now()}`, poche, reference: '', pourcentage: '0' },
                  ])
                }
              >
                + ligne {poche}
              </button>
            ))}
            <button className="bouton" disabled={occupe} onClick={() => void enregistrer()}>
              Enregistrer
            </button>
            <button className="bouton bouton--fantome" onClick={() => setEdition(null)}>
              Annuler
            </button>
          </div>
        </Bloc>
      )}

      <Bloc titre="Actions">
        <div className="barre-actions">
          {peutEditer && !edition && (
            <button className="bouton" onClick={commencerEdition}>
              Modifier les lignes
            </button>
          )}
          {peutEditer && !edition && (
            <button
              className="bouton"
              disabled={occupe || anomalies.length > 0}
              onClick={() => void agir(() => api.post(`/allocations/${a.id}/soumettre`))}
            >
              Soumettre à validation
            </button>
          )}
          {peutValider && (
            <>
              <button
                className="bouton"
                disabled={occupe || estAuteur}
                onClick={() => void agir(() => api.post(`/allocations/${a.id}/valider`))}
              >
                Valider et mettre en vigueur
              </button>
              <button
                className="bouton bouton--fantome"
                disabled={occupe}
                onClick={() => {
                  const motif = window.prompt('Motif du rejet');
                  if (motif) void agir(() => api.post(`/allocations/${a.id}/rejeter`, { motif }));
                }}
              >
                Rejeter
              </button>
            </>
          )}
        </div>
        {a.statut === 'VALIDEE' && (
          <p className="consigne consigne--pied">
            Version en vigueur depuis le {formaterDate(a.dateEffet)}. La version précédente est
            passée en « remplacée » : une seule allocation fait foi à un instant donné.
          </p>
        )}
      </Bloc>
    </>
  );
}
